import React, { useEffect, useMemo, useState } from "react";
import { useUserContext } from "../../utils/UserContext";
import { mapToPresentable, mapejos } from "../../utils/Mapejos";
import { formatAddress, getNomEmpresaVisible } from "../../utils/Utilitats";
import { aplicarValidacio, validarCodiPostal, validarEmail, validarTelefon } from "../../utils/Validacions";
import FitxaRecursos from "./FitxaRecursos";
import { idMagatzem } from "./Seleccionarmagatzem";
import { Camp, CampImatge, ChipActiu, InterruptorActiu, Valor, cridaRecurs, useImatgeRecurs } from "./RecursosComuns";

const BUIT = {
  Nom: "",
  Codi: "",
  Telefon: "",
  Email: "",
  Responsable_Usuaris_idUsuaris: "",
  TipusVia: "",
  NomVia: "",
  NumVia: "",
  EscalaVia: "",
  Pis: "",
  Porta: "",
  Poblacio: "",
  Provincia: "",
  Pais: "Espanya",
  CodiPostal: "",
  Referencies: "",
  Actiu: true,
  Observacions: "",
};

const esActiu = (m) => Number(m.Actiu) === 1;

function formDesDeRegistre(m) {
  const form = { ...BUIT };
  Object.keys(BUIT).forEach((clau) => {
    if (clau === "Actiu") return;
    form[clau] = m[clau] === null || m[clau] === undefined ? "" : String(m[clau]);
  });
  form.Actiu = esActiu(m);
  return form;
}

function Detallsmagatzem({ mode, record, llista, meta, onNavigate, onCancel, onCanviarMode, onSave, onEstatCanviat }) {
  const { empresaSeleccionada } = useUserContext();
  const readOnly = mode === "consultar" || mode === "desactivar" || mode === "reactivar";
  const [form, setForm] = useState(BUIT);
  const imatge = useImatgeRecurs();
  const { reinicia } = imatge;

  useEffect(() => {
    if (record && mode !== "nou") {
      setForm(formDesDeRegistre(record));
      reinicia(record.Imatge);
    } else {
      setForm({ ...BUIT });
      reinicia("");
    }
  }, [record, mode, reinicia]);

  const canvia = (clau) => (e) => setForm((prev) => ({ ...prev, [clau]: e.target.value }));

  const tipusViaOptions = meta?.tipusVia?.length ? meta.tipusVia : Object.keys(mapejos.tipusVia);
  const nomEmpresa = record?.EmpresaNom || getNomEmpresaVisible(empresaSeleccionada) || "—";

  // Responsables possibles: usuaris actius de l'empresa (+ l'actual encara que estigui inactiu).
  const responsables = useMemo(() => {
    const actual = Number(form.Responsable_Usuaris_idUsuaris) || null;
    return (meta?.usuaris || []).filter((u) => Number(u.Actiu) === 1 || Number(u.idUsuaris) === actual);
  }, [meta, form.Responsable_Usuaris_idUsuaris]);

  const etiquetaUsuari = (u) => (u.Nom && u.Nom !== u.NomUsuari ? `${u.Nom} (${u.NomUsuari})` : u.NomUsuari);
  const nomResponsable = record?.ResponsableNom || "";

  const validar = () => {
    const errors = [];
    const avisos = [];
    if (!form.Nom.trim()) errors.push("El camp Nom és obligatori.");
    if (!form.TipusVia) errors.push("El Tipus de via és obligatori.");
    if (!form.NomVia.trim()) errors.push("El Nom de la via és obligatori.");
    if (!form.Poblacio.trim()) errors.push("El camp Població és obligatori.");
    if (!form.Provincia.trim()) errors.push("El camp Província és obligatori.");
    if (!form.Pais.trim()) errors.push("El camp País és obligatori.");
    if (!form.CodiPostal.trim()) errors.push("El camp Codi postal és obligatori.");
    aplicarValidacio(errors, avisos, "Telèfon", validarTelefon(form.Telefon));
    aplicarValidacio(errors, avisos, "Email", validarEmail(form.Email));
    aplicarValidacio(errors, avisos, "Codi postal", validarCodiPostal(form.CodiPostal));
    return errors;
  };

  const desar = async () => {
    const errors = validar();
    if (errors.length > 0) {
      alert(`Errors trobats:\n${errors.join("\n")}`);
      return;
    }

    let nomImatge;
    try {
      nomImatge = await imatge.desa();
    } catch (error) {
      alert(`Error en pujar la imatge: ${error.message}`);
      return;
    }

    const payload = {
      ...form,
      idEmpresa: empresaSeleccionada?.IdEmpresa || null,
      Nom: form.Nom.trim(),
      Actiu: form.Actiu ? 1 : 0,
      Imatge: nomImatge,
      Responsable_Usuaris_idUsuaris: form.Responsable_Usuaris_idUsuaris || null,
    };
    if (mode === "modificar") payload.idMagatzems = record.idMagatzems;

    const resposta = await cridaRecurs(mode === "nou" ? "NouMagatzem.php" : "ModificarMagatzem.php", payload);
    if (resposta.ok) {
      onSave?.(resposta.data.magatzem);
    } else if (!resposta.silenci) {
      alert(`Error en desar el magatzem: ${resposta.message}`);
    }
  };

  const text = (clau, maxLength, extra = {}) =>
    readOnly ? (
      <Valor>{form[clau]}</Valor>
    ) : (
      <input type="text" className="dv2-input" value={form[clau]} maxLength={maxLength} onChange={canvia(clau)} {...extra} />
    );

  const enllac = (valor, prefix) =>
    valor ? (
      <a className="dv2-value" href={`${prefix}${prefix === "tel:" ? valor.replace(/[^\d+]/g, "") : valor}`}>
        {valor}
      </a>
    ) : (
      <Valor>{""}</Valor>
    );

  return (
    <FitxaRecursos
      mode={mode}
      record={record}
      llista={llista}
      idDe={idMagatzem}
      onNavigate={onNavigate}
      onCancel={onCancel}
      onCanviarMode={onCanviarMode}
      onDesar={desar}
      onEstatCanviat={onEstatCanviat}
      estat={{ endpoint: "ActivarMagatzem.php", idCamp: "idMagatzems", campActiu: "Actiu" }}
      esActiu={esActiu}
      icona="🏭"
      nomSingular="magatzem"
      nomArticle="el magatzem"
      subtitolNou="Indica el nom i l'adreça. La ubicació logística es crea automàticament."
      subtitolRegistre={form.Nom || "Dades del magatzem"}
      pestanyaAjuda="magatzems"
    >
      <section className="dv2-card">
        <div className="dv2-card-head">
          <span className="dv2-card-num">1</span>
          <h2 className="dv2-card-title">Identificació</h2>
        </div>
        <div className="dv2-fields dv2-fields--sizes">
          <Camp label="Nom" required readOnly={readOnly} mida="llarg">
            {text("Nom", 100, { autoFocus: mode === "nou" })}
          </Camp>
          <Camp label="Codi" readOnly={readOnly} mida="mig">
            {text("Codi", 30)}
          </Camp>
          <Camp label="Imatge" readOnly={readOnly} mida="ample">
            <CampImatge imatge={imatge} readOnly={readOnly} alt={`Imatge del magatzem ${form.Nom}`} icona="🏭" />
          </Camp>
        </div>
      </section>

      <section className="dv2-card">
        <div className="dv2-card-head">
          <span className="dv2-card-num">2</span>
          <h2 className="dv2-card-title">Relacions</h2>
        </div>
        <div className="dv2-fields dv2-fields--sizes">
          <Camp label="Empresa" readOnly mida="llarg">
            <Valor>{nomEmpresa}</Valor>
          </Camp>
          <Camp label="Responsable" readOnly={readOnly} mida="llarg">
            {readOnly ? (
              <Valor>{nomResponsable}</Valor>
            ) : (
              <select
                className="dv2-select"
                value={form.Responsable_Usuaris_idUsuaris}
                onChange={canvia("Responsable_Usuaris_idUsuaris")}
              >
                <option value="">— Sense responsable —</option>
                {responsables.map((u) => (
                  <option key={u.idUsuaris} value={u.idUsuaris}>
                    {etiquetaUsuari(u)}
                  </option>
                ))}
              </select>
            )}
          </Camp>
        </div>
      </section>

      <section className="dv2-card">
        <div className="dv2-card-head">
          <span className="dv2-card-num">3</span>
          <h2 className="dv2-card-title">Adreça</h2>
        </div>
        {readOnly ? (
          <div className="dv2-fields dv2-fields--sizes">
            <Camp label="Adreça" readOnly mida="ample">
              <Valor>{formatAddress(form) === "—" ? "" : formatAddress(form)}</Valor>
            </Camp>
            <Camp label="Província" readOnly>
              <Valor>{form.Provincia}</Valor>
            </Camp>
            <Camp label="País" readOnly>
              <Valor>{form.Pais}</Valor>
            </Camp>
            <Camp label="Referències" readOnly mida="ample">
              <Valor>{form.Referencies}</Valor>
            </Camp>
          </div>
        ) : (
          <div className="dv2-fields dv2-fields--sizes">
            <Camp label="Tipus de via" required>
              <select className="dv2-select" value={form.TipusVia} onChange={canvia("TipusVia")}>
                <option value="">Selecciona</option>
                {tipusViaOptions.map((t) => (
                  <option key={t} value={t}>
                    {mapToPresentable(t, mapejos.tipusVia)}
                  </option>
                ))}
              </select>
            </Camp>
            <Camp label="Nom de la via" required mida="llarg">
              {text("NomVia", 100)}
            </Camp>
            <Camp label="Número" mida="curt">
              {text("NumVia", 15)}
            </Camp>
            <Camp label="Escala" mida="curt">
              {text("EscalaVia", 10)}
            </Camp>
            <Camp label="Pis" mida="curt">
              {text("Pis", 25)}
            </Camp>
            <Camp label="Porta" mida="curt">
              {text("Porta", 5)}
            </Camp>
            <Camp label="Població" required>
              {text("Poblacio", 100)}
            </Camp>
            <Camp label="Província" required>
              {text("Provincia", 50)}
            </Camp>
            <Camp label="País" required>
              {text("Pais", 100)}
            </Camp>
            <Camp label="Codi postal" required mida="curt">
              {text("CodiPostal", 10)}
            </Camp>
            <Camp label="Referències" mida="ample">
              <input type="text" className="dv2-input" value={form.Referencies} maxLength={255} onChange={canvia("Referencies")} />
            </Camp>
          </div>
        )}
      </section>

      <section className="dv2-card">
        <div className="dv2-card-head">
          <span className="dv2-card-num">4</span>
          <h2 className="dv2-card-title">Contacte</h2>
        </div>
        <div className="dv2-fields dv2-fields--sizes">
          <Camp label="Telèfon" readOnly={readOnly}>
            {readOnly ? enllac(form.Telefon, "tel:") : <input type="tel" className="dv2-input" value={form.Telefon} maxLength={30} onChange={canvia("Telefon")} />}
          </Camp>
          <Camp label="Email" readOnly={readOnly} mida="llarg">
            {readOnly ? enllac(form.Email, "mailto:") : <input type="email" className="dv2-input" value={form.Email} maxLength={100} onChange={canvia("Email")} />}
          </Camp>
        </div>
      </section>

      <section className="dv2-card">
        <div className="dv2-card-head">
          <span className="dv2-card-num">5</span>
          <h2 className="dv2-card-title">Altres</h2>
        </div>
        <div className="dv2-fields dv2-fields--sizes">
          <Camp label="Actiu" readOnly={readOnly} mida="curt">
            {readOnly ? (
              <ChipActiu actiu={form.Actiu} />
            ) : (
              <InterruptorActiu valor={form.Actiu} onChange={(v) => setForm((prev) => ({ ...prev, Actiu: v }))} />
            )}
          </Camp>
          <Camp label="Observacions" readOnly={readOnly} mida="ample">
            {readOnly ? (
              <Valor>{form.Observacions}</Valor>
            ) : (
              <textarea className="dv2-textarea" value={form.Observacions} maxLength={255} onChange={canvia("Observacions")} />
            )}
          </Camp>
        </div>
      </section>
    </FitxaRecursos>
  );
}

export default Detallsmagatzem;
